/**
 * AnalyticsDashboard - Dashboard Analytics Avanzata
 * Grafici interattivi per trend vendite, top prodotti, clienti e distribuzione ordini
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import { getErrorMessage, formatCurrency, formatNumber } from '@/lib/utils';
import axios from 'axios';
import { toast } from 'sonner';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  AreaChart
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import {
  TrendingUp,
  TrendingDown,
  Download,
  FileSpreadsheet,
  FileText,
  ShoppingCart,
  DollarSign,
  Package,
  Users,
  Calendar,
  BarChart3,
  PieChart as PieChartIcon,
  Loader2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Colori per i grafici
const COLORS = ['#16a34a', '#2563eb', '#7c3aed', '#db2777', '#ea580c', '#dc2626', '#0891b2', '#4f46e5'];
const STATUS_COLORS: Record<string, string> = {
  delivered: '#16a34a',
  shipped: '#2563eb',
  ready: '#7c3aed',
  in_preparation: '#f59e0b',
  confirmed: '#0891b2',
  submitted: '#6366f1',
  draft: '#9ca3af',
  cancelled: '#ef4444'
};

interface SalesTrendData {
  data: Array<{ date: string; revenue: number; orders: number }>;
  summary: {
    total_revenue: number;
    total_orders: number;
    avg_daily_revenue: number;
    avg_order_value: number;
    period_days: number;
  };
}

interface TopProduct {
  product_id: string;
  name: string;
  quantity: number;
  revenue: number;
  orders_count: number;
}

interface TopCustomer {
  customer_id: string;
  name: string;
  orders_count: number;
  total_spent: number;
  avg_order_value: number;
}

interface OrderStatus {
  status: string;
  label: string;
  count: number;
  percentage: number;
  [key: string]: string | number;
}

interface MonthlyRevenue {
  month: string;
  month_label: string;
  revenue: number;
  orders: number;
  growth: number;
}

const AnalyticsDashboard: React.FC = () => {
  const { user, isSeller } = useAuth();
  const { t } = useI18n();
  
  const [period, setPeriod] = useState('30');
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState<string | null>(null);
  const [menuConfig, setMenuConfig] = useState<any>(null);
  
  // Dati analytics
  const [salesTrend, setSalesTrend] = useState<SalesTrendData | null>(null);
  const [topProducts, setTopProducts] = useState<TopProduct[]>([]);
  const [topCustomers, setTopCustomers] = useState<TopCustomer[]>([]);
  const [ordersByStatus, setOrdersByStatus] = useState<OrderStatus[]>([]);
  const [monthlyRevenue, setMonthlyRevenue] = useState<MonthlyRevenue[]>([]);
  
  // Dati Groupage (per utenti associazione)
  const [groupageStats, setGroupageStats] = useState<any>(null);
  const [isGroupageMode, setIsGroupageMode] = useState(false);

  // Verifica se in modalità groupage
  React.useEffect(() => {
    const checkGroupageMode = async () => {
      try {
        const res = await axios.get(`${API}/associations/my-association/menu`);
        if (res.data.is_association_user && res.data.menu?.analytics?.custom_mode === 'groupage') {
          setIsGroupageMode(true);
          setMenuConfig(res.data.menu);
        }
      } catch {
        // Non è utente associazione
      }
    };
    checkGroupageMode();
  }, []);
  
  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const days = parseInt(period);
      
      // Fetch tutti i dati in parallelo
      const [trendRes, statusRes, monthlyRes] = await Promise.all([
        axios.get(`${API}/analytics/sales-trend?days=${days}`),
        axios.get(`${API}/analytics/orders-by-status?days=${days}`),
        axios.get(`${API}/analytics/monthly-revenue?months=12`)
      ]);
      
      setSalesTrend(trendRes.data);
      setOrdersByStatus(statusRes.data.data);
      setMonthlyRevenue(monthlyRes.data.data);
      
      // Solo per seller: top prodotti e clienti
      if (isSeller) {
        const [productsRes, customersRes] = await Promise.all([
          axios.get(`${API}/analytics/top-products?days=${days}&limit=10`),
          axios.get(`${API}/analytics/top-customers?days=${days}&limit=10`)
        ]);
        setTopProducts(productsRes.data.data);
        setTopCustomers(customersRes.data.data);
      }
      
      // Carica statistiche groupage per utenti associazione
      if (isGroupageMode) {
        try {
          const groupageRes = await axios.get(`${API}/groupage/analytics/stats?days=${days}`);
          setGroupageStats(groupageRes.data);
        } catch {
          // Ignora errori groupage stats
        }
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error(getErrorMessage(error, 'Errore nel caricamento analytics'));
    } finally {
      setLoading(false);
    }
  }, [period, isSeller, isGroupageMode]);
  
  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);
  
  const handleExport = async (format: 'excel' | 'pdf') => {
    setExporting(format);
    try {
      const endpoint = format === 'excel' ? 'export/excel' : 'export/pdf';
      const res = await axios.get(`${API}/analytics/${endpoint}?days=${period}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const ext = format === 'excel' ? 'xlsx' : 'pdf';
      link.setAttribute('download', `analytics_${new Date().toISOString().slice(0,10)}.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Report ${format.toUpperCase()} scaricato!`);
    } catch (error) {
      toast.error(getErrorMessage(error, `Errore nell'export ${format}`));
    } finally {
      setExporting(null);
    }
  };
  
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(value);
  };
  
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-background border rounded-lg shadow-lg p-3">
          <p className="font-medium">{label}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} style={{ color: entry.color }}>
              {entry.name}: {entry.name.includes('Fatturato') || entry.name.includes('revenue') 
                ? formatCurrency(entry.value) 
                : entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="space-y-6 p-6">
        <div className="flex justify-between items-center">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-10 w-32" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <Skeleton key={i} className="h-28" />
          ))}
        </div>
        <Skeleton className="h-80" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="analytics-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BarChart3 className="h-6 w-6 text-primary" />
            Analytics Dashboard
          </h1>
          <p className="text-muted-foreground">
            Analisi dettagliata delle performance
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-40" data-testid="period-select">
              <Calendar className="h-4 w-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Ultimi 7 giorni</SelectItem>
              <SelectItem value="30">Ultimi 30 giorni</SelectItem>
              <SelectItem value="90">Ultimi 90 giorni</SelectItem>
              <SelectItem value="365">Ultimo anno</SelectItem>
            </SelectContent>
          </Select>
          
          <Button
            variant="outline"
            onClick={() => handleExport('excel')}
            disabled={!!exporting}
            data-testid="export-excel-btn"
          >
            {exporting === 'excel' ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <FileSpreadsheet className="h-4 w-4 mr-2" />
            )}
            Excel
          </Button>
          
          <Button
            variant="outline"
            onClick={() => handleExport('pdf')}
            disabled={!!exporting}
            data-testid="export-pdf-btn"
          >
            {exporting === 'pdf' ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <FileText className="h-4 w-4 mr-2" />
            )}
            PDF
          </Button>
        </div>
      </div>
      
      {/* KPI Cards */}
      {salesTrend && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card data-testid="kpi-revenue">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Fatturato Totale</p>
                  <p className="text-2xl font-bold">{formatCurrency(salesTrend.summary.total_revenue)}</p>
                </div>
                <div className="h-12 w-12 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                  <DollarSign className="h-6 w-6 text-green-600" />
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Media giornaliera: {formatCurrency(salesTrend.summary.avg_daily_revenue)}
              </p>
            </CardContent>
          </Card>
          
          <Card data-testid="kpi-orders">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Ordini Totali</p>
                  <p className="text-2xl font-bold">{formatNumber(salesTrend.summary.total_orders)}</p>
                </div>
                <div className="h-12 w-12 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                  <ShoppingCart className="h-6 w-6 text-blue-600" />
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Nel periodo selezionato
              </p>
            </CardContent>
          </Card>
          
          <Card data-testid="kpi-avg-order">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Valore Medio Ordine</p>
                  <p className="text-2xl font-bold">{formatCurrency(salesTrend.summary.avg_order_value)}</p>
                </div>
                <div className="h-12 w-12 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center">
                  <TrendingUp className="h-6 w-6 text-purple-600" />
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Per ordine completato
              </p>
            </CardContent>
          </Card>
          
          <Card data-testid="kpi-products">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">
                    {isSeller ? 'Prodotti Venduti' : 'Prodotti Ordinati'}
                  </p>
                  <p className="text-2xl font-bold">
                    {formatNumber(isSeller ? topProducts.length : ordersByStatus.reduce((a, b) => a + b.count, 0))}
                  </p>
                </div>
                <div className="h-12 w-12 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center">
                  <Package className="h-6 w-6 text-orange-600" />
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {isSeller ? 'Prodotti unici' : 'Stati diversi'}
              </p>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Sezione Groupage Analytics (solo per utenti associazione in modalità groupage) */}
      {isGroupageMode && groupageStats && (
        <Card className="border-primary/50 bg-primary/5">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="h-5 w-5 text-primary" />
              Analytics Groupage
            </CardTitle>
            <CardDescription>
              Statistiche sulle opportunità di acquisto collettivo della tua associazione
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="text-center p-4 bg-background rounded-lg border">
                <div className="text-3xl font-bold text-primary">
                  {groupageStats.summary?.active_opportunities || 0}
                </div>
                <div className="text-sm text-muted-foreground">Groupage Attivi</div>
              </div>
              <div className="text-center p-4 bg-background rounded-lg border">
                <div className="text-3xl font-bold text-green-600">
                  {groupageStats.summary?.completed_opportunities || 0}
                </div>
                <div className="text-sm text-muted-foreground">Completati</div>
              </div>
              <div className="text-center p-4 bg-background rounded-lg border">
                <div className="text-3xl font-bold text-blue-600">
                  {groupageStats.summary?.unique_participants || 0}
                </div>
                <div className="text-sm text-muted-foreground">Partecipanti</div>
              </div>
              <div className="text-center p-4 bg-background rounded-lg border">
                <div className="text-3xl font-bold text-purple-600">
                  {formatCurrency(groupageStats.summary?.total_value || 0)}
                </div>
                <div className="text-sm text-muted-foreground">Valore Totale</div>
              </div>
            </div>
            
            {groupageStats.top_sellers?.length > 0 && (
              <div className="mt-4">
                <h4 className="font-medium mb-3">Top Fornitori Groupage</h4>
                <div className="space-y-2">
                  {groupageStats.top_sellers.map((seller: any, index: number) => (
                    <div key={seller.seller_id} className="flex items-center justify-between p-2 bg-background rounded border">
                      <div className="flex items-center gap-3">
                        <span className="w-6 h-6 rounded-full bg-primary/20 text-primary flex items-center justify-center text-sm font-medium">
                          {index + 1}
                        </span>
                        <span className="font-medium">{seller.seller_name}</span>
                      </div>
                      <div className="flex items-center gap-4 text-sm">
                        <span className="text-muted-foreground">{seller.groupages_count} groupage</span>
                        <span className="font-medium text-primary">{formatCurrency(seller.total_value)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
      
      {/* Grafici principali */}
      <Tabs defaultValue="trend" className="space-y-4">
        <TabsList>
          <TabsTrigger value="trend" data-testid="tab-trend">
            <TrendingUp className="h-4 w-4 mr-2" />
            Trend Vendite
          </TabsTrigger>
          <TabsTrigger value="monthly" data-testid="tab-monthly">
            <BarChart3 className="h-4 w-4 mr-2" />
            Fatturato Mensile
          </TabsTrigger>
          <TabsTrigger value="status" data-testid="tab-status">
            <PieChartIcon className="h-4 w-4 mr-2" />
            Stati Ordini
          </TabsTrigger>
          {isSeller && (
            <>
              <TabsTrigger value="products" data-testid="tab-products">
                <Package className="h-4 w-4 mr-2" />
                Top Prodotti
              </TabsTrigger>
              <TabsTrigger value="customers" data-testid="tab-customers">
                <Users className="h-4 w-4 mr-2" />
                Top Clienti
              </TabsTrigger>
            </>
          )}
        </TabsList>
        
        {/* Trend Vendite */}
        <TabsContent value="trend">
          <Card>
            <CardHeader>
              <CardTitle>Trend Vendite Giornaliero</CardTitle>
              <CardDescription>
                Andamento fatturato e numero ordini negli ultimi {period} giorni
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={salesTrend?.data || []}>
                    <defs>
                      <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#16a34a" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#16a34a" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis 
                      dataKey="date" 
                      tickFormatter={(value) => {
                        const date = new Date(value);
                        return date.toLocaleDateString('it-IT', { day: '2-digit', month: 'short' });
                      }}
                      className="text-xs"
                    />
                    <YAxis 
                      yAxisId="left"
                      tickFormatter={(value) => `€${value >= 1000 ? `${(value/1000).toFixed(0)}k` : value}`}
                      className="text-xs"
                    />
                    <YAxis 
                      yAxisId="right" 
                      orientation="right"
                      className="text-xs"
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Area
                      yAxisId="left"
                      type="monotone"
                      dataKey="revenue"
                      name="Fatturato"
                      stroke="#16a34a"
                      fill="url(#colorRevenue)"
                      strokeWidth={2}
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="orders"
                      name="Ordini"
                      stroke="#2563eb"
                      strokeWidth={2}
                      dot={false}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Fatturato Mensile */}
        <TabsContent value="monthly">
          <Card>
            <CardHeader>
              <CardTitle>Fatturato Mensile</CardTitle>
              <CardDescription>
                Andamento degli ultimi 12 mesi con variazione percentuale
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={monthlyRevenue}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis dataKey="month_label" className="text-xs" />
                    <YAxis 
                      tickFormatter={(value) => `€${value >= 1000 ? `${(value/1000).toFixed(0)}k` : value}`}
                      className="text-xs"
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar 
                      dataKey="revenue" 
                      name="Fatturato" 
                      fill="#16a34a" 
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              
              {/* Growth indicators */}
              <div className="mt-4 flex flex-wrap gap-2">
                {monthlyRevenue.slice(-3).map((m, i) => (
                  <div 
                    key={m.month}
                    className="flex items-center gap-2 bg-muted/50 rounded-lg px-3 py-2"
                  >
                    <span className="text-sm">{m.month_label}:</span>
                    <span className={`flex items-center text-sm font-medium ${
                      m.growth > 0 ? 'text-green-600' : m.growth < 0 ? 'text-red-600' : 'text-muted-foreground'
                    }`}>
                      {m.growth > 0 ? <TrendingUp className="h-4 w-4 mr-1" /> : 
                       m.growth < 0 ? <TrendingDown className="h-4 w-4 mr-1" /> : null}
                      {m.growth > 0 ? '+' : ''}{m.growth}%
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Distribuzione Stati */}
        <TabsContent value="status">
          <Card>
            <CardHeader>
              <CardTitle>Distribuzione Ordini per Stato</CardTitle>
              <CardDescription>
                Ripartizione degli ordini nel periodo selezionato
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={ordersByStatus}
                        dataKey="count"
                        nameKey="label"
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        label={(props: any) => 
                          `${props.name || ''} (${((props.percent || 0) * 100).toFixed(0)}%)`
                        }
                        labelLine={false}
                      >
                        {ordersByStatus.map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={STATUS_COLORS[entry.status] || COLORS[index % COLORS.length]} 
                          />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                
                <div className="space-y-3">
                  {ordersByStatus.map((status, index) => (
                    <div key={status.status} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: STATUS_COLORS[status.status] || COLORS[index % COLORS.length] }}
                        />
                        <span>{status.label}</span>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="font-medium">{status.count}</span>
                        <span className="text-muted-foreground text-sm w-12 text-right">
                          {status.percentage}%
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Top Prodotti (solo seller) */}
        {isSeller && (
          <TabsContent value="products">
            <Card>
              <CardHeader>
                <CardTitle>Top 10 Prodotti per Fatturato</CardTitle>
                <CardDescription>
                  I prodotti che hanno generato più ricavi nel periodo
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={topProducts} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis 
                        type="number"
                        tickFormatter={(value) => `€${value >= 1000 ? `${(value/1000).toFixed(0)}k` : value}`}
                        className="text-xs"
                      />
                      <YAxis 
                        type="category" 
                        dataKey="name" 
                        width={150}
                        tick={{ fontSize: 12 }}
                        tickFormatter={(value) => value.length > 20 ? `${value.slice(0, 20)}...` : value}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="revenue" name="Fatturato" fill="#16a34a" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                
                {/* Tabella dettagliata */}
                <div className="mt-4 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2">Prodotto</th>
                        <th className="text-right py-2">Quantità</th>
                        <th className="text-right py-2">Fatturato</th>
                        <th className="text-right py-2">N. Ordini</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topProducts.map((product, i) => (
                        <tr key={product.product_id} className="border-b last:border-0">
                          <td className="py-2">
                            <span className="text-muted-foreground mr-2">#{i + 1}</span>
                            {product.name}
                          </td>
                          <td className="text-right py-2">{formatNumber(product.quantity)}</td>
                          <td className="text-right py-2 font-medium">{formatCurrency(product.revenue)}</td>
                          <td className="text-right py-2 text-muted-foreground">{formatNumber(product.orders_count)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}
        
        {/* Top Clienti (solo seller) */}
        {isSeller && (
          <TabsContent value="customers">
            <Card>
              <CardHeader>
                <CardTitle>Top 10 Clienti per Volume</CardTitle>
                <CardDescription>
                  I clienti che hanno speso di più nel periodo
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={topCustomers} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis 
                        type="number"
                        tickFormatter={(value) => `€${value >= 1000 ? `${(value/1000).toFixed(0)}k` : value}`}
                        className="text-xs"
                      />
                      <YAxis 
                        type="category" 
                        dataKey="name" 
                        width={150}
                        tick={{ fontSize: 12 }}
                        tickFormatter={(value) => value.length > 20 ? `${value.slice(0, 20)}...` : value}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="total_spent" name="Totale Speso" fill="#2563eb" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                
                {/* Tabella dettagliata */}
                <div className="mt-4 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2">Cliente</th>
                        <th className="text-right py-2">Ordini</th>
                        <th className="text-right py-2">Totale Speso</th>
                        <th className="text-right py-2">Media Ordine</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topCustomers.map((customer, i) => (
                        <tr key={customer.customer_id} className="border-b last:border-0">
                          <td className="py-2">
                            <span className="text-muted-foreground mr-2">#{i + 1}</span>
                            {customer.name}
                          </td>
                          <td className="text-right py-2">{formatNumber(customer.orders_count)}</td>
                          <td className="text-right py-2 font-medium">{formatCurrency(customer.total_spent)}</td>
                          <td className="text-right py-2 text-muted-foreground">{formatCurrency(customer.avg_order_value)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

export default AnalyticsDashboard;
